import { useState, useRef, useEffect } from "react";
import { useGT } from "gt-react";
import { MoreVertical, Download, Upload, Server, KeyRound, Check, ChevronRight } from "lucide-react";
import {
  useAppStore,
  useServersStore,
  exportData,
  exportServerData,
  importData,
} from "../store";
import { toast } from "./Toasts";
import { APP_LANGUAGE_LABELS, APP_LOCALES, useAppLocale } from "../i18n";
import { parsePostmanCollection, parseAltairExport, detectExternalFormat } from "../importExternal";

export function AppMenu() {
  const [open, setOpen] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showImportMenu, setShowImportMenu] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const setShowVaultManager = useAppStore((s) => s.setShowVaultManager);
  const setShowRuntimeDialog = useAppStore((s) => s.setShowRuntimeDialog);
  const setImportPreview = useAppStore((s) => s.setImportPreview);
  const activeServerId = useServersStore((s) => s.activeServerId);
  const servers = useServersStore((s) => s.servers);
  const activeServer = servers.find((s) => s.id === activeServerId);
  const t = useGT();
  const { locale, setLocale } = useAppLocale();
  const secretsConfigLabel = locale === "de" ? "Secrets & Konfiguration" : t("Secrets & Config");

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const handleExportAll = async () => {
    setOpen(false);
    const data = await exportData();
    downloadJson(data, `spuerhund-export-${datestamp()}.json`);
    toast(t("Data exported"), "success");
  };

  const handleExportServer = async () => {
    if (!activeServerId) return;
    setOpen(false);
    const data = await exportServerData(activeServerId);
    const slug = (activeServer?.name ?? "server").toLowerCase().replace(/[^a-z0-9]+/g, "-");
    downloadJson(data, `spuerhund-${slug}-${datestamp()}.json`);
    toast(t('Exported "{name}"', { name: activeServer?.name ?? "server" }), "success");
  };

  const handleImportSpuerhund = () => {
    setOpen(false);
    pickJsonFile(async (text) => {
      const data = JSON.parse(text);
      if (data.version !== 1 && data.version !== 2) {
        toast(t("Invalid export file format"), "error");
        return;
      }
      await importData(data);
      toast(t("Data imported. Servers merged by name."), "success");
    });
  };

  const handleImportPostman = () => {
    setOpen(false);
    pickJsonFile((text) => {
      const data = JSON.parse(text);
      const result = parsePostmanCollection(data);
      setImportPreview(result);
    });
  };

  const handleImportAltair = () => {
    setOpen(false);
    pickJsonFile((text) => {
      const data = JSON.parse(text);
      const result = parseAltairExport(data);
      setImportPreview(result);
    });
  };

  const handleImportAuto = () => {
    setOpen(false);
    pickJsonFile(async (text) => {
      const data = JSON.parse(text);

      // Native Spürhund format
      if (data.version === 1 || data.version === 2) {
        await importData(data);
        toast(t("Data imported. Servers merged by name."), "success");
        return;
      }

      // Try external format detection
      const format = detectExternalFormat(data);
      if (format === "postman") {
        setImportPreview(parsePostmanCollection(data));
      } else if (format === "altair") {
        setImportPreview(parseAltairExport(data));
      } else {
        toast(t("Unknown file format. Supported: Spürhund, Postman, Altair."), "error");
      }
    });
  };

  const handleOpenVault = () => {
    setOpen(false);
    setShowVaultManager(true);
  };

  const handleOpenRuntime = () => {
    setOpen(false);
    setShowRuntimeDialog(true);
  };

  const handleSetLocale = (nextLocale: typeof locale) => {
    setLocale(nextLocale);
    setOpen(false);
    setShowLanguageMenu(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => {
          setOpen((v) => {
            const nextOpen = !v;
            if (!nextOpen) {
              setShowLanguageMenu(false);
              setShowImportMenu(false);
            }
            return nextOpen;
          });
        }}
        className="rounded p-1.5 text-zinc-500 hover:bg-surface-raised hover:text-zinc-300"
        title={t("Menu")}
      >
        <MoreVertical size={14} />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 min-w-[220px] rounded-lg border border-border bg-surface-raised py-1 shadow-xl">
          <button
            onClick={() => setShowLanguageMenu((value) => !value)}
            className="flex w-full items-center justify-start gap-2 px-3 py-1.5 text-left text-xs text-zinc-400 hover:bg-surface-overlay hover:text-zinc-200"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <Check size={12} className="text-success opacity-0" />
            </span>
            <span className="min-w-0 flex-1 text-left">{t("Language")}</span>
            <span className="text-[10px] text-zinc-600">{APP_LANGUAGE_LABELS[locale]}</span>
            <ChevronRight size={12} className={`shrink-0 transition-transform ${showLanguageMenu ? "rotate-90" : ""}`} />
          </button>
          {showLanguageMenu && (
            <div className="pb-1">
              {APP_LOCALES.map((entry) => (
                <button
                  key={entry}
                  onClick={() => handleSetLocale(entry)}
                  className="flex w-full items-center justify-start gap-2 px-3 py-1.5 pl-7 text-left text-xs text-zinc-400 hover:bg-surface-overlay hover:text-zinc-200"
                >
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    {locale === entry ? <Check size={12} className="text-success" /> : null}
                  </span>
                  <span className="min-w-0 text-left">{APP_LANGUAGE_LABELS[entry]}</span>
                </button>
              ))}
            </div>
          )}
          <div className="my-1 border-t border-border" />
          <button
            onClick={handleExportAll}
            className="flex w-full items-center justify-start gap-2 px-3 py-1.5 text-left text-xs text-zinc-400 hover:bg-surface-overlay hover:text-zinc-200"
          >
            <Download size={12} />
            <span className="min-w-0 text-left">{t("Export All")}</span>
          </button>
          <button
            onClick={handleExportServer}
            disabled={!activeServerId}
            className="flex w-full items-center justify-start gap-2 px-3 py-1.5 text-left text-xs text-zinc-400 hover:bg-surface-overlay hover:text-zinc-200 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Server size={12} />
            <span className="min-w-0 text-left">{t("Export Current Server")}</span>
          </button>
          <div className="my-1 border-t border-border" />
          {/* Import submenu */}
          <button
            onClick={() => setShowImportMenu((v) => !v)}
            className="flex w-full items-center justify-start gap-2 px-3 py-1.5 text-left text-xs text-zinc-400 hover:bg-surface-overlay hover:text-zinc-200"
          >
            <Upload size={12} />
            <span className="min-w-0 flex-1 text-left">{t("Import")}</span>
            <ChevronRight size={12} className={`shrink-0 transition-transform ${showImportMenu ? "rotate-90" : ""}`} />
          </button>
          {showImportMenu && (
            <div className="pb-1">
              <button
                onClick={handleImportAuto}
                className="flex w-full items-center justify-start gap-2 px-3 py-1.5 pl-7 text-left text-xs text-zinc-400 hover:bg-surface-overlay hover:text-zinc-200"
              >
                <Upload size={10} />
                <span className="min-w-0 text-left">{t("Auto-detect")}</span>
              </button>
              <button
                onClick={handleImportSpuerhund}
                className="flex w-full items-center justify-start gap-2 px-3 py-1.5 pl-7 text-left text-xs text-zinc-400 hover:bg-surface-overlay hover:text-zinc-200"
              >
                <span className="min-w-0 text-left">{t("Spürhund")}</span>
              </button>
              <button
                onClick={handleImportPostman}
                className="flex w-full items-center justify-start gap-2 px-3 py-1.5 pl-7 text-left text-xs text-zinc-400 hover:bg-surface-overlay hover:text-zinc-200"
              >
                <span className="min-w-0 text-left">{t("Postman Collection")}</span>
              </button>
              <button
                onClick={handleImportAltair}
                className="flex w-full items-center justify-start gap-2 px-3 py-1.5 pl-7 text-left text-xs text-zinc-400 hover:bg-surface-overlay hover:text-zinc-200"
              >
                <span className="min-w-0 text-left">{t("Altair GraphQL")}</span>
              </button>
            </div>
          )}
          <div className="my-1 border-t border-border" />
          <button
            onClick={handleOpenVault}
            className="flex w-full items-center justify-start gap-2 px-3 py-1.5 text-left text-xs text-zinc-400 hover:bg-surface-overlay hover:text-zinc-200"
          >
            <KeyRound size={12} />
            <span className="min-w-0 text-left">{secretsConfigLabel}</span>
          </button>
          <button
            onClick={handleOpenRuntime}
            className="flex w-full items-center justify-start gap-2 px-3 py-1.5 text-left text-xs text-zinc-400 hover:bg-surface-overlay hover:text-zinc-200"
          >
            <Server size={12} />
            <span className="min-w-0 text-left">{t("Local Runtime")}</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function datestamp() {
  return new Date().toISOString().slice(0, 10);
}

function downloadJson(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function pickJsonFile(onContent: (text: string) => void | Promise<void>) {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".json";
  input.onchange = async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      await onContent(text);
    } catch (err) {
      toast(
        err instanceof Error ? err.message : "Failed to import data",
        "error",
      );
    }
  };
  input.click();
}
